import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import ActionButton from '@repo/ui/action-button'
import Button from '@repo/ui/button'
import { BuildingTwoIcon, DeleteIcon, PlusIcon, UserRemoveIcon } from '@repo/ui/icons'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'

import {
  assignCenterTeachingHall,
  unassignCenterTeachingHall
} from '../../../../api/centers/center-teaching-hall/assign-center-teaching-hall'
import { Paths } from '../../../../lib/constants/path'
import { useObserver } from '../../../../lib/contexts/observer'
import useAuth from '../../../../lib/hooks/use-auth'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import { RoleEnum } from '../../../../lib/types/enums/users/role.enum'
import { TeachingHall } from '../../../../lib/types/models/ol/teaching-hall/teaching-hall'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import CenterTeachingHallForm from '../../ol-common/ol-center-program/ol-center-teaching-hall/center-teaching-hall-form'
import CenterTeachingHallTable from '../../ol-common/ol-center-program/ol-center-teaching-hall/center-teaching-hall-table'

type Props = {
  centerId: number
  olId?: number
  canEdit?: boolean
}

const OlPersonalSpaceTeachingHallTable = ({ centerId, olId, canEdit = true }: Props) => {
  const { userRole } = useAuth()
  const olTeachingHallCrud = useObserver({ key: 'ol-teaching-hall-crud' })
  const { modalState, selectedData, closeModel, toggleModal } = useModalManager<
    'create' | 'delete',
    TeachingHall
  >(['create', 'delete'])

  const assignTeachingHallMutation = useMutation({
    mutationFn: assignCenterTeachingHall,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Teaching Hall assigned successfully')
      olTeachingHallCrud.notify(['ol-teaching-hall-table'])
      closeModel('create')
    }
  })

  const unassignTeachingHallMutation = useMutation({
    mutationFn: unassignCenterTeachingHall,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Teaching Hall unassigned successfully')
      olTeachingHallCrud.notify(['ol-teaching-hall-table'])
    }
  })

  return (
    <div>
      {canEdit && (
        <Button label="Add teaching hall" icon={PlusIcon} onClick={() => toggleModal('create')} />
      )}
      <div className="h-2" />
      <CenterTeachingHallTable
        centerId={centerId}
        navigationPath={
          userRole !== RoleEnum.Ol
            ? undefined
            : (teachingHall) =>
                Paths.TEACHING_HALL_UPDATE_PAGE.replace(':id', teachingHall.id.toString())
        }
        extraColumns={[
          {
            accessorKey: 'id',
            header: '',
            hidden: !canEdit,
            cell: ({ row }) => (
              <ActionButton
                variant={'danger'}
                icon={DeleteIcon}
                onClick={(e) => {
                  e.stopPropagation()
                  toggleModal('delete', row.original)
                }}
              />
            )
          }
        ]}
      />

      <Modal
        isOpen={modalState.create}
        title={'Add teaching hall'}
        size="medium"
        hideActions
        icon={() => <BuildingTwoIcon color={tailwindColors.primary.DEFAULT} />}
        onCancel={() => closeModel('create')}
      >
        <CenterTeachingHallForm
          className={MODAL_CONTENT_CLASSNAME}
          olId={olId}
          centerId={centerId}
          buttonsConfig={modalButtonConfig({
            cancelText: 'Close',
            confirmText: 'Confirm',
            onConfirm: async (data) => {
              await assignTeachingHallMutation.mutateAsync({ centerId, ...data })
              closeModel('create')
            },
            onCancel: () => closeModel('create')
          })}
        />
      </Modal>

      {selectedData && (
        <Modal
          isOpen={modalState.delete}
          icon={UserRemoveIcon}
          variant="destructive"
          size="small"
          title="Are you sure you want to unassign this teaching hall?"
          hasBorderBottom={false}
          isLoading={unassignTeachingHallMutation.isPending}
          onCancel={() => closeModel('delete')}
          actionButtonsOptions={{
            confirmText: 'Unassign',
            cancelText: 'Cancel',
            onConfirm: async () => {
              await unassignTeachingHallMutation.mutateAsync({
                centerId,
                teachingHallId: selectedData.id
              })
              closeModel('delete')
            }
          }}
        />
      )}
    </div>
  )
}

export default OlPersonalSpaceTeachingHallTable
